import type { ScreenOption } from '@remote/shared';

import type { DeviceEntry } from './devices.js';
import type { VideoStats } from './stats.js';

function mustGet<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (element === null) {
    throw new Error(`Falta el elemento #${id} en index.html`);
  }
  return element as T;
}

export type ConnectionLabel = RTCPeerConnectionState | 'idle';

const CONNECTION_TEXT: Record<ConnectionLabel, string> = {
  idle: 'sin conexión',
  new: 'negociando',
  connecting: 'conectando',
  connected: 'conectado',
  disconnected: 'desconectado',
  failed: 'ha fallado',
  closed: 'cerrada',
};

export interface Ui {
  log(line: string): void;
  setSignedIn(label: string): void;
  setSignedOut(): void;
  setError(message: string): void;
  renderDevices(devices: DeviceEntry[], onPick: (device: DeviceEntry) => void): void;
  setConnection(state: ConnectionLabel): void;
  setRtt(milliseconds: number | null): void;
  showSession(visible: boolean): void;
  setVideoStream(stream: MediaStream | null): void;
  setVideoStats(stats: VideoStats): void;
  setControlEnabled(enabled: boolean): void;
  setControlActive(active: boolean): void;
  onFullscreen(handler: () => void): void;
  getVideoElement(): HTMLVideoElement;
  getVideoWrap(): HTMLElement;
  getKeyboardInput(): HTMLInputElement;
  /** Muestra u oculta la barra superpuesta (en pantalla completa). */
  setOverlayVisible(visible: boolean): void;
  onOverlayExit(handler: () => void): void;
  onOverlayKeyboard(handler: () => void): void;
  /** Ajusta textos y botones según el modo (táctil vs. puntero). */
  setTouchMode(touch: boolean): void;
  /** Enfoca el input oculto para abrir el teclado software en móvil. */
  focusKeyboard(): void;
  onToggleControl(handler: () => void): void;
  onKeyboard(handler: () => void): void;
  setScreens(screens: ScreenOption[], activeId: string): void;
  onScreenPick(handler: (screenId: string) => void): void;
  onSignIn(handler: () => void): void;
  onSignOut(handler: () => void): void;
  onPing(handler: () => void): void;
  onHangUp(handler: () => void): void;
}

export function createUi(): Ui {
  const logView = mustGet<HTMLPreElement>('log');
  const authStateView = mustGet<HTMLElement>('auth-state');
  const deviceList = mustGet<HTMLUListElement>('devices');
  const emptyState = mustGet<HTMLElement>('devices-empty');
  const sessionPanel = mustGet<HTMLElement>('session');
  const videoWrap = mustGet<HTMLElement>('video-wrap');
  const videoElement = mustGet<HTMLVideoElement>('remote-video');
  const videoStatsBar = mustGet<HTMLElement>('video-stats');
  const videoOverlay = mustGet<HTMLElement>('video-overlay');
  const overlayExit = mustGet<HTMLButtonElement>('overlay-exit');
  const overlayKeyboard = mustGet<HTMLButtonElement>('overlay-keyboard');
  const connectionView = mustGet<HTMLElement>('connection-state');
  const rttView = mustGet<HTMLElement>('rtt');
  const signInButton = mustGet<HTMLButtonElement>('sign-in');
  const signOutButton = mustGet<HTMLButtonElement>('sign-out');
  const pingButton = mustGet<HTMLButtonElement>('ping');
  const hangUpButton = mustGet<HTMLButtonElement>('hang-up');
  const takeControlButton = mustGet<HTMLButtonElement>('take-control');
  const fullscreenButton = mustGet<HTMLButtonElement>('fullscreen');
  const keyboardButton = mustGet<HTMLButtonElement>('keyboard');
  const keyboardInput = mustGet<HTMLInputElement>('keyboard-input');
  const controlHint = mustGet<HTMLElement>('control-hint');
  const screenPickerWrap = mustGet<HTMLElement>('screen-picker-wrap');
  const screenPicker = mustGet<HTMLSelectElement>('screen-picker');

  const log = (line: string): void => {
    const stamp = new Date().toLocaleTimeString('es-ES', { hour12: false });
    logView.textContent = `${logView.textContent ?? ''}${stamp}  ${line}\n`;
    logView.scrollTop = logView.scrollHeight;
  };

  return {
    log,

    setSignedIn(label) {
      authStateView.textContent = label;
      authStateView.dataset['tone'] = 'ok';
      signInButton.hidden = true;
      signOutButton.hidden = false;
    },

    setSignedOut() {
      authStateView.textContent = 'sin iniciar sesión';
      authStateView.dataset['tone'] = 'warn';
      signInButton.hidden = false;
      signInButton.disabled = false;
      signOutButton.hidden = true;
      deviceList.replaceChildren();
      emptyState.hidden = false;
      emptyState.textContent = 'Inicia sesión para ver tus dispositivos.';
    },

    setError(message) {
      authStateView.textContent = message;
      authStateView.dataset['tone'] = 'error';
      signInButton.disabled = false;
    },

    renderDevices(devices, onPick) {
      deviceList.replaceChildren();

      if (devices.length === 0) {
        emptyState.hidden = false;
        emptyState.textContent =
          'Ningún dispositivo registrado. Arranca el agente en tu Mac e inicia sesión con la misma cuenta.';
        return;
      }

      emptyState.hidden = true;

      for (const device of devices) {
        const item = document.createElement('li');

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'device';
        button.disabled = !device.record.online;

        const dot = document.createElement('span');
        dot.className = 'dot';
        dot.dataset['online'] = String(device.record.online);

        const name = document.createElement('span');
        name.className = 'device-name';
        name.textContent = device.record.name;

        const meta = document.createElement('span');
        meta.className = 'device-meta';
        meta.textContent = device.record.online
          ? 'encendido'
          : `visto ${formatLastSeen(device.record.lastSeen)}`;

        button.append(dot, name, meta);
        button.addEventListener('click', () => onPick(device));

        item.append(button);
        deviceList.append(item);
      }
    },

    setConnection(state) {
      connectionView.textContent = CONNECTION_TEXT[state];
      connectionView.dataset['tone'] =
        state === 'connected' ? 'ok' : state === 'failed' ? 'error' : 'idle';
      pingButton.disabled = state !== 'connected';
    },

    setRtt(milliseconds) {
      rttView.textContent =
        milliseconds === null ? '—' : `${milliseconds.toFixed(1)} ms`;
    },

    showSession(visible) {
      sessionPanel.hidden = !visible;
    },

    setVideoStream(stream) {
      if (stream === null) {
        videoElement.srcObject = null;
        videoWrap.hidden = true;
        videoStatsBar.textContent = '—';
        screenPickerWrap.hidden = true;
        return;
      }
      videoElement.srcObject = stream;
      videoWrap.hidden = false;
    },

    setControlEnabled(enabled) {
      takeControlButton.disabled = !enabled;
      // La pantalla completa solo-ver también depende de que haya vídeo.
      fullscreenButton.disabled = !enabled;
    },

    onFullscreen(handler) {
      fullscreenButton.addEventListener('click', () => handler());
    },

    setControlActive(active) {
      takeControlButton.textContent = active ? 'Soltar control' : 'Tomar control';
      takeControlButton.dataset['active'] = String(active);
      // El botón de teclado (móvil) solo tiene sentido con el control activo.
      if (keyboardButton.dataset['available'] === 'true') {
        keyboardButton.hidden = !active;
      }
    },

    getVideoElement() {
      return videoElement;
    },

    getVideoWrap() {
      return videoWrap;
    },

    getKeyboardInput() {
      return keyboardInput;
    },

    setOverlayVisible(visible) {
      videoOverlay.hidden = !visible;
      // El botón de teclado del overlay solo en modo táctil.
      overlayKeyboard.hidden = keyboardButton.dataset['available'] !== 'true';
    },

    onOverlayExit(handler) {
      overlayExit.addEventListener('click', () => handler());
    },

    onOverlayKeyboard(handler) {
      overlayKeyboard.addEventListener('click', () => handler());
    },

    setTouchMode(touch) {
      // En móvil el teclado físico no existe: se ofrece el botón de teclado
      // software, y la ayuda cambia porque no hay tecla Esc.
      keyboardButton.dataset['available'] = String(touch);
      if (touch) {
        controlHint.innerHTML =
          'Toca para hacer clic, arrastra para mover el cursor, desliza con dos dedos ' +
          'para desplazar. Usa <b>Teclado</b> para escribir y <b>Soltar control</b> para salir.';
      }
    },

    focusKeyboard() {
      // El focus tiene que ocurrir dentro del gesto del usuario para que el móvil
      // abra el teclado; por eso es una acción explícita y no automática.
      keyboardInput.focus();
    },

    onToggleControl(handler) {
      takeControlButton.addEventListener('click', () => handler());
    },

    onKeyboard(handler) {
      keyboardButton.addEventListener('click', () => handler());
    },

    setScreens(screens, activeId) {
      // Con una sola pantalla no hay nada que elegir; se oculta el selector.
      if (screens.length <= 1) {
        screenPickerWrap.hidden = true;
        return;
      }
      screenPicker.replaceChildren();
      for (const screen of screens) {
        const option = document.createElement('option');
        option.value = screen.id;
        option.textContent = screen.name;
        option.selected = screen.id === activeId;
        screenPicker.append(option);
      }
      screenPickerWrap.hidden = false;
    },

    onScreenPick(handler) {
      screenPicker.addEventListener('change', () => handler(screenPicker.value));
    },

    setVideoStats(stats) {
      const parts: string[] = [];
      if (stats.width !== null && stats.height !== null) {
        parts.push(`${String(stats.width)}×${String(stats.height)}`);
      }
      if (stats.codec !== null) parts.push(stats.codec);
      if (stats.fps !== null) parts.push(`${String(Math.round(stats.fps))} fps`);
      if (stats.bitrateKbps !== null) {
        parts.push(`${(stats.bitrateKbps / 1000).toFixed(1)} Mbps`);
      }
      if (stats.rttMs !== null) parts.push(`RTT ${stats.rttMs.toFixed(1)} ms`);
      if (stats.packetsLost !== null) parts.push(`${String(stats.packetsLost)} perdidos`);
      videoStatsBar.textContent = parts.length > 0 ? parts.join('  ·  ') : '—';
    },

    onSignIn(handler) {
      signInButton.addEventListener('click', () => {
        signInButton.disabled = true;
        handler();
      });
    },

    onSignOut(handler) {
      signOutButton.addEventListener('click', () => handler());
    },

    onPing(handler) {
      pingButton.addEventListener('click', () => handler());
    },

    onHangUp(handler) {
      hangUpButton.addEventListener('click', () => handler());
    },
  };
}

function formatLastSeen(timestamp: number): string {
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 60) return 'hace unos segundos';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `hace ${String(minutes)} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `hace ${String(hours)} h`;
  return new Date(timestamp).toLocaleDateString('es-ES');
}
